/**
 * Phase 86 Plan 86-03 Task 2 — the "Compost" design's five-block decay meter
 * plus its adjacent age text (the "ROT · LAST TOUCH" cell in the handoff).
 *
 * Bucketing reads the SAME threshold constants `taskRotZone` reads
 * (`TASK_WARM_THRESHOLD_DAYS` / `TASK_STALE_THRESHOLD_DAYS` /
 * `TASK_GRAVEYARD_THRESHOLD_DAYS`, all from `utils/format.ts`) so the day
 * numbers can never drift — but applies them to the FLOORED day count
 * rather than `taskRotZone`'s continuous value. This is deliberate: the
 * handoff's meter has six buckets (today / 1d / 2-5d / >5d / >15d /
 * terminal), one more than `taskRotZone`'s five zones, because "today" and
 * "1d ago" both sit inside the single `fresh` zone but render different
 * fill counts. Driving that split off the continuous zone boundary would
 * make the "1d ago" bucket nearly unreachable — by the time real wall-clock
 * time is checked, a task touched exactly one day ago has almost always
 * already ticked past `taskRotZone`'s `daysQuiet > 1` cutoff into `warm`.
 * Flooring first avoids that race entirely.
 *
 * Accessibility (README "Accessibility" section):
 *   - The whole meter carries `role="img"` with a single composite
 *     accessible description built from the age (e.g. "Last active 23
 *     days ago — abandoned") — the five blocks and the age text underneath
 *     are `aria-hidden`, so a screen reader gets one clean announcement
 *     instead of the blocks, the age text and a redundant emoji all
 *     narrating the same state separately. Colour is never the only
 *     signal: the visible age text still repeats it for sighted users.
 *     `MyTaskRow`'s TASK cell owns the separate visible+accessible emoji
 *     (sourced from `taskStalenessEmoji`) inline before the title — this
 *     component deliberately does not duplicate that announcement.
 *   - Terminal rows render no meter at all — the "clock stopped" copy only.
 *   - The blocks stay static (the handoff is explicit: no motion on the meter).
 */
import {
  TASK_GRAVEYARD_THRESHOLD_DAYS,
  TASK_STALE_THRESHOLD_DAYS,
  TASK_WARM_THRESHOLD_DAYS,
} from '../../utils/format';
import {
  FRESH_DARK_HEX,
  FRESH_HEX,
  GRAVEYARD_DARK_HEX,
  GRAVEYARD_HEX,
  METER_UNFILLED_HEX,
  STALE_DARK_HEX,
  STALE_HEX,
  WARM_METER_FILLED_HEX,
} from './compostTokens';

export interface MtRotMeterProps {
  lastActivityAt: string | null | undefined;
  isTerminal?: boolean;
  className?: string;
}

type MeterBucket = 'today' | 'oneDay' | 'warm' | 'stale' | 'graveyard' | 'terminal';

interface BucketConfig {
  filledCount: number;
  filledColor: string;
  graveLid: boolean;
  ageText: string;
  ageColor: string;
  ageWeightClass: string;
  ariaLabel: string;
}

function daysQuietFrom(lastActivityAt: string | null | undefined): number {
  if (!lastActivityAt) return 0;
  const last = new Date(lastActivityAt);
  if (Number.isNaN(last.getTime())) return 0;
  return Math.max(0, (Date.now() - last.getTime()) / 86_400_000);
}

function resolveBucket(daysQuiet: number, isTerminal: boolean | undefined): MeterBucket {
  if (isTerminal) return 'terminal';
  const ageDays = Math.floor(daysQuiet);
  if (ageDays <= 0) return 'today';
  if (ageDays <= TASK_WARM_THRESHOLD_DAYS) return 'oneDay';
  if (ageDays <= TASK_STALE_THRESHOLD_DAYS) return 'warm';
  if (ageDays <= TASK_GRAVEYARD_THRESHOLD_DAYS) return 'stale';
  return 'graveyard';
}

function bucketConfig(bucket: MeterBucket, daysQuiet: number): BucketConfig {
  const ageDays = Math.floor(daysQuiet);
  switch (bucket) {
    case 'today':
      return {
        filledCount: 5,
        filledColor: FRESH_HEX,
        graveLid: false,
        ageText: 'today',
        ageColor: FRESH_DARK_HEX,
        ageWeightClass: 'font-medium',
        ariaLabel: 'Last active today',
      };
    case 'oneDay':
      return {
        filledCount: 4,
        filledColor: FRESH_HEX,
        graveLid: false,
        ageText: '1d ago',
        ageColor: FRESH_DARK_HEX,
        ageWeightClass: 'font-medium',
        ariaLabel: 'Last active 1 day ago',
      };
    case 'warm':
      return {
        filledCount: 2,
        filledColor: WARM_METER_FILLED_HEX,
        graveLid: false,
        ageText: `${ageDays}d ago`,
        ageColor: 'var(--ms-text-muted)',
        ageWeightClass: 'font-medium',
        ariaLabel: `Last active ${ageDays} days ago`,
      };
    case 'stale':
      return {
        filledCount: 1,
        filledColor: STALE_HEX,
        graveLid: false,
        ageText: `${ageDays}d ago`,
        ageColor: STALE_DARK_HEX,
        ageWeightClass: 'font-bold',
        ariaLabel: `Last active ${ageDays} days ago — getting stale`,
      };
    case 'graveyard':
      return {
        filledCount: 0,
        filledColor: METER_UNFILLED_HEX,
        graveLid: true,
        ageText: `${ageDays}d ago`,
        ageColor: GRAVEYARD_DARK_HEX,
        ageWeightClass: 'font-bold',
        ariaLabel: `Last active ${ageDays} days ago — abandoned`,
      };
    case 'terminal':
    default:
      return {
        filledCount: 0,
        filledColor: METER_UNFILLED_HEX,
        graveLid: false,
        ageText: 'clock stopped',
        ageColor: 'var(--ms-text-muted)',
        ageWeightClass: 'font-normal',
        ariaLabel: 'Activity clock stopped — done work never rots',
      };
  }
}

export function MtRotMeter({ lastActivityAt, isTerminal = false, className = '' }: MtRotMeterProps) {
  const daysQuiet = daysQuietFrom(lastActivityAt);
  const bucket = resolveBucket(daysQuiet, isTerminal);
  const cfg = bucketConfig(bucket, daysQuiet);

  if (bucket === 'terminal') {
    // No meter, no emoji, no `role="img"` — there is nothing to abstract
    // into a single image-like announcement; the plain text already
    // carries the (only) meaning: the clock stopped.
    return (
      <div className={`flex items-center gap-2 ${className}`} data-testid="mt-rot-meter">
        <span
          className="font-mono text-[10px] font-normal"
          style={{ color: cfg.ageColor }}
          data-testid="mt-rot-meter-age"
        >
          {cfg.ageText}
        </span>
      </div>
    );
  }

  return (
    <div
      role="img"
      aria-label={cfg.ariaLabel}
      className={`flex items-center gap-2 ${className}`}
      data-testid="mt-rot-meter"
    >
      <span className="flex gap-0.5" aria-hidden="true" data-testid="mt-rot-meter-blocks">
        {Array.from({ length: 5 }).map((_, i) => (
          <span
            key={i}
            data-testid={`mt-rot-meter-block-${i}`}
            className="block w-1.5 h-3"
            style={{
              backgroundColor: i < cfg.filledCount ? cfg.filledColor : METER_UNFILLED_HEX,
              borderTop: i === 0 && cfg.graveLid ? `2px solid ${GRAVEYARD_HEX}` : undefined,
            }}
          />
        ))}
      </span>
      <span
        aria-hidden="true"
        className={`font-mono text-[10px] ${cfg.ageWeightClass} tabular-nums`}
        style={{ color: cfg.ageColor }}
        data-testid="mt-rot-meter-age"
      >
        {cfg.ageText}
      </span>
    </div>
  );
}
